import React from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { WorkflowDevFooter } from '../../../base'
import { inputProperties } from '../../../types/inputProperties'
import { NavigationButtons } from '../navigation-buttons'
import TabbedPaymentMethod, {
  TabbedPaymentMethodProps,
} from './components/TabbedPaymentMethod'

/**
 * @param {inputProperties} props.line1 (optional) - properties for field
 * @param {inputProperties} props.line2 (optional) - properties for field
 * @param {inputProperties} props.city (optional) - properties for field
 * @param {inputProperties} props.state (optional) - properties for field
 * @param {inputProperties} props.postalCode (optional) - properties for field
 * @param {inputProperties} props.country (optional) - properties for field
 * @param {inputProperties} cardholderName (optional) - properties for field
 * @param {inputProperties} cardNumber (optional) - properties for field
 * @param {inputProperties} country (optional) - properties for field
 * @param {inputProperties} cvv (optional) - properties for field
 * @param {inputProperties} exp (optional) - properties for field
 * @param {UseFormReturn} form (Optional) - hook for managing forms with ease
 */
export interface PaymentMethodProps extends TabbedPaymentMethodProps {
  line1?: inputProperties
  line2?: inputProperties
  city?: inputProperties
  state?: inputProperties
  postalCode?: inputProperties
  country?: inputProperties
  cardholderName?: inputProperties
  cardNumber?: inputProperties
  cvv?: inputProperties
  exp?: inputProperties
  form?: UseFormReturn
  onClickBack?: Function
  onSubmit?: Function
  theme?: object
}

/**
 * @see {@link PaymentMethodProps} extends {@link TabbedPaymentMethodProps}
 * @param {inputProperties} props.line1 (optional) - properties for field
 * @param {inputProperties} props.line2 (optional) - properties for field
 * @param {inputProperties} props.city (optional) - properties for field
 * @param {inputProperties} props.state (optional) - properties for field
 * @param {inputProperties} props.postalCode (optional) - properties for field
 * @param {inputProperties} props.country (optional) - properties for field
 * @param {inputProperties} cardholderName (optional) - properties for field
 * @param {inputProperties} cardNumber (optional) - properties for field
 * @param {inputProperties} country (optional) - properties for field
 * @param {inputProperties} cvv (optional) - properties for field
 * @param {inputProperties} exp (optional) - properties for field
 * @param {UseFormReturn} props.form (Optional) - hook for managing forms with ease
 * @param {Function} props.onClickBack (optional) - Callback fired when the user clicks on back button
 * @returns
 */
const PaymentMethod = (props: PaymentMethodProps) => {
  const useFormDefault = props.form || useForm()
  const cardholderNameProps = Object.assign(
    PaymentMethod.defaultProps.cardholderName,
    props.cardholderName,
  )
  const cardNumberProps = Object.assign(
    PaymentMethod.defaultProps.cardNumber,
    props.cardNumber,
  )
  const expProps = Object.assign(PaymentMethod.defaultProps.exp, props.exp)
  const cvvProps = Object.assign(PaymentMethod.defaultProps.cvv, props.cvv)
  Object.assign(PaymentMethod.defaultProps.address.country, props.country)
  Object.assign(PaymentMethod.defaultProps.address.line1, props.line1)
  Object.assign(PaymentMethod.defaultProps.address.line2, props.line2)
  Object.assign(PaymentMethod.defaultProps.address.city, props.city)
  Object.assign(PaymentMethod.defaultProps.address.state, props.state)
  Object.assign(PaymentMethod.defaultProps.address.postalCode, props.postalCode)
  const theme = createTheme(props.theme)

  const {
    formState: { isSubmitting },
  } = useFormDefault

  const onClickContinue = () => {
    props.onSubmit && props.onSubmit()
  }
  const onClickBack = () => {
    props.onClickBack && props.onClickBack()
  }

  return (
    <>
      <ThemeProvider theme={theme}>
        <TabbedPaymentMethod
          {...PaymentMethod.defaultProps.address}
          cardholderName={cardholderNameProps}
          cardNumber={cardNumberProps}
          exp={expProps}
          cvv={cvvProps}
          form={useFormDefault}
          tabs={props.tabs}
          titleLabel={props.titleLabel}
        />
        <NavigationButtons
          isSubmitting={isSubmitting}
          onClickBack={onClickBack}
          onClickContinue={onClickContinue}
        />
        <WorkflowDevFooter />
      </ThemeProvider>
    </>
  )
}

PaymentMethod.defaultProps = {
  cardholderName: {
    required: true,
    name: 'cardHolderName',
  },
  cardNumber: {
    required: true,
    name: 'cardNumber',
  },
  cvv: {
    required: true,
    name: 'cvv',
  },
  exp: {
    required: true,
    name: 'exp',
  },
  address: {
    country: {
      name: 'country',
      required: true,
    },
    line1: {
      name: 'line1',
      required: true,
    },
    line2: {
      name: 'line2',
    },
    city: {
      name: 'city',
      required: true,
    },
    state: {
      name: 'state',
      required: true,
    },
    postalCode: {
      name: 'postalCode',
      required: true,
    },
  },
  theme: {},
}

export default PaymentMethod
